import {
  CategoriesAndTopics,
  CategoriesTableType,
  Category,
  ServicesTableType
} from '../../../types/Admin/CategoriesTopicsTypes';
import { mapItemIconFields } from './mapItemIconFields';
import { mapChildrenIconFields } from './mapChildrenIconFields';
import { mapExtraInputFields } from './mapExtraInputFields';

const DEFAULT_BRANCH = "default";
const defaultLower = DEFAULT_BRANCH.toLowerCase();

const filterDefaultBranches = (branches?: { id: number; name: string }[]) =>
  branches?.filter(
    (b) => b.id !== 0 && b.name.trim().toLowerCase() !== defaultLower,
  );

export const getCategoriesOrTopicsData = (categoriesAndTopicsData: CategoriesAndTopics) => {
  const topicsArr: ServicesTableType[] = [];
  const subCategoriesArr: CategoriesTableType[] = [];

  categoriesAndTopicsData.categories.forEach((category: Category) => {
    subCategoriesArr.push({
      id: category.id,
      nameRu: category.nameRu,
      categoryCounter: '0',
      topicCounter: category.topics.length.toString(),
      nameBe: category.nameBe,
      nameEn: category.nameEn,
      startFinishWork: `${category.startWork}-${category.endWork}`,
      radioType: 'section',
      isActive: category.isActive,
      topics: category.topics,
      isParentCategory: category.isParentCategory,
      ...(!category.isParentCategory && { parentCategory: category.parentCategory }),
      ...(category.branches && { branches: filterDefaultBranches(category.branches) }),
      ...(category.queues && { queues: category.queues }),
      ...mapItemIconFields(category),
      ...mapChildrenIconFields(category)
    });
  });

  const processCategory = (category: Category) => {
    if (category.topics && category.topics.length > 0) {
      category.topics.forEach((topic) => {
        topicsArr.push({
          id: topic.id,
          nameRu: topic.nameRu,
          categoryCounter: '0',
          topicCounter: '0',
          nameBe: topic.nameBe,
          nameEn: topic.nameEn,
          radioType: 'service',
          startFinishWork: `${topic.startWork}-${topic.endWork}`,
          isActiveTopic: topic.isActiveTopic,
          itemNamePrefix: topic.itemNamePrefix,
          ...(topic.branches && { branches: filterDefaultBranches(topic.branches) }),
          ...(topic.queues && { queues: topic.queues }),
          ...mapItemIconFields(topic),
          ...mapChildrenIconFields(topic),
          ...mapExtraInputFields(topic)
        });
      });
    }
    if (category.subCategories) {
      category.subCategories.forEach((subCategory: Category) => {
        subCategoriesArr.push({
          id: subCategory.id,
          nameRu: subCategory.nameRu,
          categoryCounter: '0',
          topicCounter: subCategory.topics.length.toString(),
          nameBe: subCategory.nameBe,
          nameEn: subCategory.nameEn,
          startFinishWork: `${subCategory.startWork}-${subCategory.endWork}`,
          radioType: 'section',
          isActive: subCategory.isActive,
          topics: subCategory.topics,
          isParentCategory: subCategory.isParentCategory,
          ...(!subCategory.isParentCategory && { parentCategory: subCategory.parentCategory }),
          ...(subCategory.branches && { branches: filterDefaultBranches(subCategory.branches) }),
          ...(subCategory.queues && { queues: subCategory.queues }),
          ...mapItemIconFields(subCategory),
          ...mapChildrenIconFields(subCategory)
        });
        processCategory(subCategory);
      });
    }
  };

  categoriesAndTopicsData?.categories?.forEach((category: Category) => {
    processCategory(category);
  });

  if (categoriesAndTopicsData.unusedTopics && categoriesAndTopicsData.unusedTopics.length > 0) {
    categoriesAndTopicsData.unusedTopics.forEach((topic) => {
      topicsArr.push({
        id: topic.id,
        nameRu: topic.nameRu,
        categoryCounter: '0',
        topicCounter: '0',
        nameBe: topic.nameBe,
        nameEn: topic.nameEn,
        radioType: 'service',
        startFinishWork: `${topic.startWork}-${topic.endWork}`,
        isActiveTopic: topic.isActiveTopic,
        itemNamePrefix: topic.itemNamePrefix,
        ...(topic.branches && { branches: filterDefaultBranches(topic.branches) }),
        ...(topic.queues && { queues: topic.queues }),
        ...mapItemIconFields(topic),
        ...mapChildrenIconFields(topic),
        ...mapExtraInputFields(topic)
      });
    });
  }

  const mergeById = <T extends { id: number }>(a?: T[], b?: T[]): T[] | undefined => {
    if (!a?.length && !b?.length) return undefined;
    const map = new Map<number, T>();
    (a ?? []).forEach((x) => map.set(x.id, x));
    (b ?? []).forEach((x) => map.set(x.id, x));
    return Array.from(map.values());
  };

  const mergeBindings = <
    T extends {
      id: number;
      branches?: { id: number }[];
      queues?: { id: number }[];
      iconUrl?: string | null;
      childrenIconUrl?: string | null;
    },
  >(
    existing: T,
    curr: T,
  ): T => {
    const iconSource = existing.iconUrl ? existing : curr.iconUrl ? curr : existing;
    const childrenIconSource = existing.childrenIconUrl
      ? existing
      : curr.childrenIconUrl
        ? curr
        : existing;
    return {
      ...existing,
      ...(mergeById(existing.branches, curr.branches) && {
        branches: mergeById(existing.branches, curr.branches),
      }),
      ...(mergeById(existing.queues, curr.queues) && {
        queues: mergeById(existing.queues, curr.queues),
      }),
      ...mapItemIconFields(iconSource),
      ...mapChildrenIconFields(childrenIconSource),
    };
  };

  const dedupeRowsById = <T extends { id: number }>(rows: T[], merge: (a: T, b: T) => T): T[] =>
    rows.reduce((acc: T[], curr: T) => {
      const idx = acc.findIndex((row) => row.id === curr.id);
      if (idx === -1) return [...acc, curr];
      const next = acc.slice();
      next[idx] = merge(acc[idx], curr);
      return next;
    }, []);

  // Важно: одна и та же услуга/категория (по id) может прийти несколько раз
  // (например, из разных разделов или при «разрезании» по очередям/филиалам).
  // Если просто "удалить дубликаты", можно потерять часть очередей.
  const uniqueSectionsArr = dedupeRowsById(subCategoriesArr, mergeBindings);
  const uniqueTopicsArr = dedupeRowsById(topicsArr, mergeBindings);

  return { categoriesArr: uniqueSectionsArr, topicsArr: uniqueTopicsArr };
};
